"use client";

import type { ContactInfo, ContactLabels } from "@/components/landing/Contact/types";
import type { ContactContent } from "@/components/landing/registry/content";
import type { PublicSectionProps } from "@/components/landing/registry/types";
import ContactForm from "@/components/landing/Contact/ContactForm";
import ContactInfoPanel from "@/components/landing/Contact/ContactInfoPanel";
import { pickBi } from "@/components/landing/registry/locale";
import {
	GlowBackground,
	MotionReveal,
	SectionShell,
	useSectionMotion,
} from "@/components/landing/shared";

// Only title/intro/channel values are admin-editable via the Content tab
// (`ContactContent`); every other label here (badge, feature rows, form
// field labels, success/error copy) has no schema source, so it's hardcoded
// verbatim from the previous `landing.contact.*` i18n copy.
const STRINGS = {
	en: {
		badge: "Contact us",
		features: [
			{ title: "We're here to help", body: "Our team usually replies within one business day." },
			{ title: "Email us anytime", body: "Drop us a message and we'll get back to you." },
			{
				title: "Support that cares",
				body: "From setup to scale, we're with you every step of the way.",
			},
		],
		name: "Your name",
		email: "Email address",
		subject: "Subject",
		message: "How can we help?",
		send: "Send message",
		sending: "Sending…",
		success: "Your message has been sent. We will be in touch soon.",
		error: "Please check the form and try again.",
		privacy: "Your information is safe with us. We respect your privacy.",
	},
	ar: {
		badge: "تواصل معنا",
		features: [
			{ title: "نحن هنا لمساعدتك", body: "يرد فريقنا عادة خلال يوم عمل واحد." },
			{ title: "راسلنا في أي وقت", body: "أرسل لنا رسالة وسنعود إليك بالرد." },
			{ title: "دعم يهتم بك", body: "من الإعداد وحتى النمو، نحن معك في كل خطوة." },
		],
		name: "اسمك",
		email: "البريد الإلكتروني",
		subject: "الموضوع",
		message: "كيف يمكننا مساعدتك؟",
		send: "إرسال الرسالة",
		sending: "جارٍ الإرسال…",
		success: "تم إرسال رسالتك. سنتواصل معك قريباً.",
		error: "يرجى التحقق من النموذج والمحاولة مجدداً.",
		privacy: "معلوماتك بأمان معنا. نحترم خصوصيتك.",
	},
};

interface InnerProps {
	labels: ContactLabels;
	info: ContactInfo;
	locale: string;
	sectionId: string;
}

function Inner(prop: InnerProps) {
	const { labels, info, locale, sectionId } = prop;
	const motion = useSectionMotion();
	return (
		<>
			<GlowBackground
				className="-bottom-40 -inset-s-32 h-112 w-md opacity-40"
				style={{
					background: "radial-gradient(circle at 40% 40%, var(--brand-2), transparent 65%)",
				}}
			/>

			<div className="mx-auto grid max-w-6xl items-start gap-14 px-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-16">
				<MotionReveal motion={motion}>
					<ContactInfoPanel labels={labels} info={info} />
				</MotionReveal>

				<MotionReveal motion={motion} delayOffset={120}>
					<ContactForm labels={labels} locale={locale} sectionId={sectionId} />
				</MotionReveal>
			</div>
		</>
	);
}

function Contact(prop: PublicSectionProps<ContactContent>) {
	const { instance, locale } = prop;
	const { content, general, layout, style } = instance;
	const strings = locale === "ar" ? STRINGS.ar : STRINGS.en;

	const labels: ContactLabels = {
		badge: strings.badge,
		title: pickBi(content.title, locale),
		intro: pickBi(content.intro, locale),
		features: strings.features,
		name: strings.name,
		email: strings.email,
		subject: strings.subject,
		message: strings.message,
		send: strings.send,
		sending: strings.sending,
		success: strings.success,
		error: strings.error,
		privacy: strings.privacy,
	};
	const info: ContactInfo = {
		email: content.email,
		phone: content.phone,
		whatsapp: content.whatsapp,
		instagram: content.instagram,
		x: content.x,
	};

	return (
		<SectionShell
			general={general}
			layout={layout}
			style={style}
			motion={instance.motion}
			className="jana-bg-teal relative scroll-mt-20 overflow-hidden py-16 md:py-24"
		>
			<Inner labels={labels} info={info} locale={locale} sectionId={instance.id} />
		</SectionShell>
	);
}

export default Contact;
